/**
 * Pure helpers for the phone Calendar (pages/EventsCalendar.svelte,
 * components/mobile/MobileMonthView.svelte, MobileAgendaView.svelte;
 * plans/mobile-v2.md §3.6): which phone layout a URL means, the URLs of the
 * phone layouts, the Month grid (dots + span bars) and the Agenda groups.
 * Dates are `YYYY-MM-DD` strings read as calendar dates, so the viewer's
 * time zone never shifts them.
 */
import type { CalendarEvent } from './types';
import { eventColor } from './eventColors';
import {
  MS_PER_DAY,
  eventEndDate,
  eventTotalDays,
  eventsForDay,
  formatCalendarDate,
  formatEventTimeShort,
  getTodayStr,
  isMultiDayEvent,
  parseCalendarDate,
  sortEventsForDisplay,
  toDateStr,
} from './utils';
import { formatShortDateRange } from './dateRangeLabel';
import { eventTypeLabel } from './mobileDays';

export type MobileCalendarKind = 'month' | 'agenda' | 'three-days';

/** Segments of the phone layout switcher, in order. */
export const MOBILE_CALENDAR_KINDS: { value: MobileCalendarKind; label: string; icon: string }[] = [
  { value: 'month', label: 'Month', icon: '📅' },
  { value: 'agenda', label: 'Agenda', icon: '📋' },
  { value: 'three-days', label: '3 Days', icon: '🕙' },
];

/** Big Year, Sequential and Weekday have no phone layout (plans §1 decision, §3.6). */
export const DESKTOP_ONLY_CALENDAR_LAYOUTS: Record<string, string> = {
  'big-year': 'The Big Year layout',
  sequential: 'The Sequential layout',
  weekday: 'The Weekday layout',
};

/** Days of the phone 3-day window. */
export const THREE_DAYS = 3;
/** Days ahead the Agenda lists (mirrors `EventsCalendarController::AGENDA_DAYS`). */
export const AGENDA_DAYS = 90;
/** Most event-type dots a Month cell shows. */
export const MAX_MONTH_DOTS = 3;
/** Most rows of span bars a Month week draws. */
export const MAX_SPAN_LANES = 3;

export type MobileCalendarView =
  { kind: MobileCalendarKind } | { kind: 'desktop-only'; feature: string };

/**
 * The phone layout a Calendar URL means. `layout` is the page's layout prop,
 * `requested` the layout named in the URL (null when the URL names none: a phone
 * opens Month, where desktop opens Big Year). Wall is Month, Time Blocks is
 * 3 Days; Big Year, Sequential and Weekday are desktop-only (no auto-redirect, D8).
 */
export function resolveMobileCalendar(
  layout: string,
  requested: string | null | undefined,
): MobileCalendarView {
  if (layout === 'agenda') return { kind: 'agenda' };
  if (layout === 'time-blocks') return { kind: 'three-days' };
  if (layout === 'wall') return { kind: 'month' };
  const feature = DESKTOP_ONLY_CALENDAR_LAYOUTS[layout];
  if (feature && requested) return { kind: 'desktop-only', feature };
  return { kind: 'month' };
}

export interface MobileCalendarUrlState {
  year: number;
  /** Month to open, 1–12 (Month only). */
  month?: number;
  /** First day of the 3-day window (3 Days only). */
  start?: string;
}

/**
 * URL of a phone layout. Month and 3 Days reuse the desktop Wall and Time Blocks
 * layouts, Agenda has its own; none of them touches a stored preference.
 */
export function mobileCalendarUrl(kind: MobileCalendarKind, state: MobileCalendarUrlState): string {
  const params = new URLSearchParams();
  if (kind === 'month') {
    params.set('layout', 'wall');
    if (state.month) params.set('month', String(state.month));
  } else if (kind === 'agenda') {
    params.set('layout', 'agenda');
  } else {
    params.set('layout', 'time-blocks');
    params.set('start', state.start ?? getTodayStr());
    params.set('range', String(THREE_DAYS));
  }
  return `/events_calendar/${state.year}?${params.toString()}`;
}

/** The date `days` after `date` (negative: before). */
export function addDays(date: string, days: number): string {
  const d = parseCalendarDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateStr(d);
}

/** `{ year, month }` (month 1–12) one month on from `year`/`month`. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

// ── Month grid ───────────────────────────────────────────────────────────────

export interface MonthCell {
  date: string;
  day: number;
  /** False for the neighbouring months' days that fill the first / last week. */
  inMonth: boolean;
  isToday: boolean;
  /** Event-type colours of the day's single-day events, at most `MAX_MONTH_DOTS`. */
  dots: string[];
  /** Number of events on the day, multi-day ones included. */
  eventCount: number;
}

export interface MonthSpan {
  event: CalendarEvent;
  /** 0–6, inclusive. */
  startCol: number;
  endCol: number;
  lane: number;
  continuesLeft: boolean;
  continuesRight: boolean;
  color: string;
}

export interface MonthWeek {
  days: MonthCell[];
  /** Multi-day events crossing the week, at most `MAX_SPAN_LANES` rows deep. */
  spans: MonthSpan[];
  /** Rows the bar strip needs (0 when the week has no visible span). */
  laneCount: number;
}

function assignSpanLanes(spans: Omit<MonthSpan, 'lane'>[]): MonthSpan[] {
  const sorted = [...spans].sort(
    (a, b) => a.startCol - b.startCol || b.endCol - b.startCol - (a.endCol - a.startCol),
  );
  const laneEnds: number[] = [];
  return sorted.map((span) => {
    let lane = laneEnds.findIndex((end) => end < span.startCol);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = span.endCol;
    return { ...span, lane };
  });
}

/**
 * The weeks (Sunday first) of a month, each with its days' dots and the span bars
 * of its multi-day events. `month` is 1–12.
 */
export function buildMonthGrid(
  year: number,
  month: number,
  events: CalendarEvent[],
  today: string = getTodayStr(),
): MonthWeek[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  const gridStart = new Date(first);
  gridStart.setUTCDate(1 - first.getUTCDay());
  const weekCount = Math.ceil((first.getUTCDay() + last.getUTCDate()) / 7);
  const multiDay = events.filter(isMultiDayEvent);

  const weeks: MonthWeek[] = [];
  for (let w = 0; w < weekCount; w++) {
    const days: MonthCell[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(gridStart);
      d.setUTCDate(gridStart.getUTCDate() + w * 7 + i);
      const date = toDateStr(d);
      const dayEvents = eventsForDay(events, date);
      days.push({
        date,
        day: d.getUTCDate(),
        inMonth: d.getUTCMonth() === month - 1,
        isToday: date === today,
        dots: sortEventsForDisplay(dayEvents.filter((e) => !isMultiDayEvent(e)))
          .slice(0, MAX_MONTH_DOTS)
          .map((e) => eventColor(e.type)),
        eventCount: dayEvents.length,
      });
    }

    const weekStart = days[0].date;
    const weekEnd = days[6].date;
    const spans = assignSpanLanes(
      multiDay
        .filter((e) => e.date <= weekEnd && eventEndDate(e) >= weekStart)
        .map((event) => {
          const from = Math.max(0, dayOffset(weekStart, event.date));
          const to = Math.min(6, dayOffset(weekStart, eventEndDate(event)));
          return {
            event,
            startCol: from,
            endCol: to,
            continuesLeft: event.date < weekStart,
            continuesRight: eventEndDate(event) > weekEnd,
            color: eventColor(event.type),
          };
        }),
    ).filter((span) => span.lane < MAX_SPAN_LANES);
    weeks.push({
      days,
      spans,
      laneCount: spans.length ? Math.max(...spans.map((s) => s.lane)) + 1 : 0,
    });
  }
  return weeks;
}

function dayOffset(from: string, to: string): number {
  return Math.round(
    (parseCalendarDate(to).getTime() - parseCalendarDate(from).getTime()) / MS_PER_DAY,
  );
}

/** Which way a finished drag turns the month: -1 previous, 1 next, 0 not a horizontal swipe. */
export function swipeDirection(dx: number, dy: number, minPx = 48): -1 | 0 | 1 {
  if (Math.abs(dx) < minPx || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}

/**
 * The day a month opens with selected: today when it's in the month, else the 1st.
 */
export function defaultSelectedDate(
  year: number,
  month: number,
  today: string = getTodayStr(),
): string {
  const prefix = `${year}-${String(month).padStart(2, '0')}`;
  return today.startsWith(prefix) ? today : `${prefix}-01`;
}

// ── Event rows ───────────────────────────────────────────────────────────────

/**
 * The grey line of an event row: `Oct 7 – 8 · Event · 2 days` for a multi-day
 * event, `9:00 AM · Appt` (or just `Event`) for a single day's.
 */
export function eventMetaLine(event: CalendarEvent, today?: string): string {
  const type = eventTypeLabel(event.type);
  if (isMultiDayEvent(event)) {
    const range = formatShortDateRange(event.date, eventEndDate(event), today).replace(
      /(\d)–(\d)/,
      '$1 – $2',
    );
    return [range, type, `${eventTotalDays(event)} days`].join(' · ');
  }
  return [formatEventTimeShort(event), type].filter(Boolean).join(' · ');
}

/** `Wed, Oct 7` */
export function formatDayHeading(date: string): string {
  return formatCalendarDate(parseCalendarDate(date), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// ── Agenda ───────────────────────────────────────────────────────────────────

export interface AgendaGroup {
  date: string;
  /** `Today`, `Tomorrow` or null. */
  relative: string | null;
  events: CalendarEvent[];
}

/**
 * Upcoming events grouped by day, days without events left out. A multi-day event
 * is listed once, on the day it starts (or today, while it is under way), so a
 * nine-day trip is not repeated nine times.
 */
export function buildAgendaGroups(
  events: CalendarEvent[],
  today: string = getTodayStr(),
  days: number = AGENDA_DAYS,
): AgendaGroup[] {
  const lastDay = addDays(today, days - 1);
  const byDate = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    if (eventEndDate(event) < today || event.date > lastDay) continue;
    const date = event.date < today ? today : event.date;
    byDate.set(date, [...(byDate.get(date) ?? []), event]);
  }
  const tomorrow = addDays(today, 1);
  return [...byDate.keys()].sort().map((date) => ({
    date,
    relative: date === today ? 'Today' : date === tomorrow ? 'Tomorrow' : null,
    events: sortEventsForDisplay(byDate.get(date) ?? []),
  }));
}
